import { useEffect, useState } from "react";
import {
  listShares,
  revokeShare,
  shareErrorMessage,
  updateSharePermissions,
  type ShareRow,
  type ShareScope,
} from "./api";

// "Shared with" management panel — lists every outgoing user-targeted
// share for a given scope+scopeId and exposes per-row actions: change
// permission (read ↔ edit) and revoke.  Used in two places today: the
// share modal inside `ShareSection` (frontend/src/components/ItemDetail.tsx)
// and the sidebar's `GroupSharingPanel` (frontend/src/pages/Home.tsx).

export function SharedWithPanel({
  scope,
  scopeId,
  accessToken,
  rev,
  onChanged,
  variant = "default",
}: {
  scope: ShareScope;
  scopeId: string;
  accessToken: string;
  rev: number;
  onChanged: () => void;
  /** "default" wraps in a section header; "bare" renders just the list
   *  (sidebar mounts its own header so it can sit next to "Shared from"). */
  variant?: "default" | "bare";
}) {
  const [rows, setRows] = useState<ShareRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const all = await listShares(accessToken, "outgoing");
        if (!alive) return;
        setRows(
          all.filter(
            (s) =>
              s.scope === scope && s.scopeId === scopeId && !s.revokedAt,
          ),
        );
      } catch (err) {
        if (alive) setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      alive = false;
    };
  }, [accessToken, scope, scopeId, rev]);

  if (rows === null && !error) return null;
  if (rows && rows.length === 0) return null;

  const list = (
    <>
      {error && <p className="text-[11px] text-rose-600 dark:text-rose-400">{error}</p>}
      <ul className="space-y-1">
        {(rows ?? []).map((s) => (
          <SharedWithRow
            key={s.id}
            row={s}
            accessToken={accessToken}
            onChanged={onChanged}
          />
        ))}
      </ul>
    </>
  );

  if (variant === "bare") return list;

  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
        Shared with
      </span>
      {list}
    </div>
  );
}

export function SharedWithRow({
  row,
  accessToken,
  onChanged,
}: {
  row: ShareRow;
  accessToken: string;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const recipientLabel = !row.other
    ? "(unknown)"
    : "pending" in row.other
      ? row.other.email
      : row.other.handle
        ? `@${row.other.handle}`
        : row.other.display_name?.trim() || row.other.email;

  const status: { label: string; cls: string } = row.acceptedAt
    ? { label: "Active", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300" }
    : row.other && "pending" in row.other
      ? { label: "Invited", cls: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300" }
      : { label: "Pending", cls: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300" };

  const revoke = async () => {
    if (!confirm(`Revoke share with ${recipientLabel}?`)) return;
    setBusy(true);
    setError(null);
    try {
      await revokeShare(accessToken, row.id);
      onChanged();
    } catch (err) {
      setError(
        shareErrorMessage(err instanceof Error ? err.message : String(err)),
      );
    } finally {
      setBusy(false);
    }
  };

  const changePerm = async (next: "read" | "edit") => {
    setBusy(true);
    setError(null);
    try {
      await updateSharePermissions(accessToken, row.id, next);
      onChanged();
    } catch (err) {
      setError(
        shareErrorMessage(err instanceof Error ? err.message : String(err)),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="flex items-center gap-2 rounded border border-slate-200 bg-white px-2 py-1 text-[11px] dark:border-slate-700 dark:bg-slate-900">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="truncate font-medium text-slate-900 dark:text-slate-100">
            {recipientLabel}
          </span>
          <span className={`rounded px-1 py-0.5 text-[10px] ${status.cls}`}>
            {status.label}
          </span>
        </div>
        {error && (
          <div className="mt-0.5 text-[10px] text-rose-600 dark:text-rose-400">{error}</div>
        )}
      </div>
      <select
        value={row.permissions}
        onChange={(e) => void changePerm(e.target.value as "read" | "edit")}
        disabled={busy}
        className="rounded border border-slate-300 bg-white px-1 py-0.5 text-[11px] dark:border-slate-600 dark:bg-slate-800"
        aria-label="Permission level"
      >
        <option value="read">read</option>
        <option value="edit">edit</option>
      </select>
      <button
        type="button"
        onClick={() => void revoke()}
        disabled={busy}
        className="rounded px-1 py-0.5 text-[11px] text-rose-600 hover:bg-rose-50 disabled:opacity-50 dark:text-rose-400 dark:hover:bg-rose-950"
      >
        Revoke
      </button>
    </li>
  );
}
